import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * StatusDot — small 8px circle used next to labels / identity badges.
 * Pencil: StatusDot/{Active,Inactive,Pending,Error} (oa6Dk 8v8jh mFOUq ErThJ)
 */
export const statusDotVariants = cva('inline-block rounded-pill shrink-0', {
  variants: {
    status: {
      active: 'bg-ia-success',
      inactive: 'bg-ia-text-tertiary',
      pending: 'bg-ia-warning animate-pulse',
      error: 'bg-ia-error',
    },
    size: {
      sm: 'h-[6px] w-[6px]',
      md: 'h-[8px] w-[8px]',
      lg: 'h-[10px] w-[10px]',
    },
  },
  defaultVariants: { status: 'active', size: 'md' },
})

export interface StatusDotProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusDotVariants> {
  /** accessible label describing the status */
  label?: string
}

export const StatusDot = React.forwardRef<HTMLSpanElement, StatusDotProps>(
  ({ className, status, size, label, ...props }, ref) => (
    <span
      ref={ref}
      role="status"
      aria-label={label ?? status ?? 'status'}
      className={cn(statusDotVariants({ status, size }), className)}
      {...props}
    />
  ),
)
StatusDot.displayName = 'StatusDot'
